import api from "./axios";

export interface Forum {
    id: string;
    class_subject_id: string;
    teacher_id: string;
    title: string;
    description?: string | null;
    is_active: boolean;
    allow_student_posts: boolean;
    require_approval: boolean;
    created_at: string;
    updated_at: string;

    // Relationships
    class_subject?: {
        id: string;
        class?: {
            id: string;
            name: string;
            grade_level: string;
        };
        subject?: {
            id: string;
            name: string;
            code: string;
        };
        semester?: {
            id: string;
            name: string;
        };
    };
    teacher?: {
        id: string;
        user?: {
            id: string;
            name: string;
        };
    };

    // Computed attributes
    post_count?: number;
    main_post_count?: number;
    latest_post?: ForumPost | null;
}

export interface ForumPost {
    id: string;
    forum_id: string;
    parent_id?: string | null;
    user_id: string;
    title?: string | null;
    content: string;
    status: "draft" | "published" | "hidden" | "deleted";
    is_pinned: boolean;
    is_locked: boolean;
    view_count: number;
    like_count: number;
    approved_at?: string | null;
    created_at: string;
    updated_at: string;

    // Relationships
    user?: {
        id: string;
        name: string;
    };
    approved_by_teacher?: {
        id: string;
        user?: {
            id: string;
            name: string;
        };
    };
    forum?: Forum;
    replies?: ForumPost[];

    // Computed attributes
    status_text?: string;
    type?: string;
    excerpt?: string;
    is_liked_by_me?: boolean;
}

export interface ForumFilters {
    class_subject_id?: string | null;
    teacher_id?: string | null;
    is_active?: boolean | null;
    search?: string;
    page?: number;
    per_page?: number;
}

export interface CreateForumData {
    class_subject_id: string;
    teacher_id?: string;
    title: string;
    description?: string;
    is_active?: boolean;
    allow_student_posts?: boolean;
    require_approval?: boolean;
}

const forumApi = {
    async getAll(filters: ForumFilters = {}): Promise<{
        success: boolean;
        message: string;
        data: { items: Forum[]; meta: any };
    }> {
        const response = await api.get("/v1/forums", { params: filters });
        return response.data;
    },

    async getById(id: string): Promise<Forum> {
        const response = await api.get(`/v1/forums/${id}`);
        return response.data.data;
    },

    async create(data: CreateForumData): Promise<Forum> {
        const response = await api.post("/v1/forums", data);
        return response.data.data;
    },

    async update(id: string, data: Partial<CreateForumData>): Promise<Forum> {
        const response = await api.put(`/v1/forums/${id}`, data);
        return response.data.data;
    },

    async delete(id: string): Promise<void> {
        await api.delete(`/v1/forums/${id}`);
    },

    async getThreads(
        forumId: string,
        filters: {
            status?: string | null;
            page?: number;
            per_page?: number;
        } = {},
    ): Promise<{
        success: boolean;
        message: string;
        data: { items: ForumPost[]; meta: any };
    }> {
        const response = await api.get(`/v1/forums/${forumId}/posts`, {
            params: filters,
        });
        return response.data;
    },

    async createThread(
        forumId: string,
        data: { title: string; content: string },
    ): Promise<ForumPost> {
        const response = await api.post(`/v1/forums/${forumId}/posts`, data);
        return response.data.data;
    },

    async getThread(id: string): Promise<ForumPost> {
        const response = await api.get(`/v1/forum-posts/${id}`);
        return response.data.data;
    },

    async reply(id: string, content: string): Promise<ForumPost> {
        const response = await api.post(`/v1/forum-posts/${id}/reply`, {
            content,
        });
        return response.data.data;
    },

    async updatePost(
        id: string,
        data: { title?: string; content?: string },
    ): Promise<ForumPost> {
        const response = await api.put(`/v1/forum-posts/${id}`, data);
        return response.data.data;
    },

    async deletePost(id: string): Promise<void> {
        await api.delete(`/v1/forum-posts/${id}`);
    },

    async moderate(
        id: string,
        status: "published" | "hidden",
    ): Promise<ForumPost> {
        const response = await api.post(`/v1/forum-posts/${id}/moderate`, {
            status,
        });
        return response.data.data;
    },

    async togglePin(id: string): Promise<ForumPost> {
        const response = await api.post(`/v1/forum-posts/${id}/pin`);
        return response.data.data;
    },

    async toggleLock(id: string): Promise<ForumPost> {
        const response = await api.post(`/v1/forum-posts/${id}/lock`);
        return response.data.data;
    },

    async toggleLike(id: string): Promise<{
        like_count: number;
        is_liked_by_me: boolean;
    }> {
        const response = await api.post(`/v1/forum-posts/${id}/like`);
        return response.data.data;
    },
};

export default forumApi;
